import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ProcessedRcEdit from './ProcessedRcEdit';
import ProcessedPhasedEdit from './ProcessedPhasedEdit';
import ProcessedHazardForecastEdit from './ProcessedHazardForecastEdit';
import OccurrenceEdit from './OccurrenceEdit';
import AvalancheControlEdit from './AvalancheControlEdit';
import ProcessedEventTable from './ProcessedEventTable';
import { getBreakpoints } from '@awp/helpers/UIHelper';
import { debounce } from '@awp/helpers/Utils';
import { BREAD_CRUMB_NAMES, EventTypes, FORM_MODE } from '@awp/constants';
import { selectEventTypes, selectPhasedEventTypes } from '@awp/stores/eventSlice';
import { toast } from 'react-toastify';
import { useLocation, useParams } from 'react-router-dom';
import { AwpContainer } from '../Common/AwpContainer';
import { AvalancheTableFilterContextProvider } from './AvalancheTableFilterContext';
import { AvalancheEventContextProvider } from './AvalanceEventContext';

const AvalancheProcessedEvent = ({ initialMode, initialEventType, isInitialRender }) => {
  const location = useLocation();
  const { id: initialEventId } = useParams();
  const [locationState, setLocationState] = useState();
  const [fromMenu, setFromMenu] = useState(locationState && locationState.fromMenu ? true : false);
  const [isRoute, setIsRoute] = useState(false);
  const [mode, setMode] = useState(initialMode ?? FORM_MODE.LIST);
  const [title, setTitle] = useState('Advanced Search');
  const [breakpoints, setBreakpoints] = useState(getBreakpoints());
  const [loading, setLoading] = useState(false);
  const [eventId, setEventId] = useState(initialEventId ?? null);

  const [expandedRowEntityId, setExpandedRowEntityId] = useState(null);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  const eventTypes = useSelector(selectEventTypes);
  const phasedEventTypes = useSelector(selectPhasedEventTypes);

  const [eventTypeToActOn, setEventTypeToActOn] = useState(
    eventTypes.find((e) => e.value === (initialEventType ?? locationState?.eventType)),
  );
  const [eventTypeToCreate, setEventTypeToCreate] = useState(
    eventTypes.find((e) => e.value === EventTypes.AvalancheOccurrence),
  );
  // const [index, setIndex] = useState(0);
  const [sort, setSort] = useState([{ id: 'timestamp', desc: true }]);
  // const [defaultPageSize, setDefaultPageSize] = useState(25);
  const [isProcessed, setIsProcessed] = useState(true);
  const [isDuplicated, setIsDuplicated] = useState(false);

  useEffect(() => {
    const locationState = location?.state;
    setIsRoute(locationState && locationState.mode && locationState.id ? true : false);
    if (!location?.state) return;
    if (locationState.mode) setMode(locationState.mode);
    if (locationState.eventType) setEventTypeToActOn(eventTypes.find((e) => e.value === locationState.eventType));
    setLocationState(locationState);
  }, [location]);

  useEffect(() => {
    const handleResize = debounce(() => {
      setBreakpoints(getBreakpoints());
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const findEvent = (eventData, evtId) => {
    if (!eventData || eventData.length === 0) return null;
    for (const event of eventData) {
      // Check if the current event's id matches
      if (event.id === evtId) {
        return event;
      }

      // If the current event has child events, recursively search them
      if (event.ChildIngestionCandidates && event.ChildIngestionCandidates.length > 0) {
        for (const ch of event.ChildIngestionCandidates) {
          const child = JSON.parse(ch.JsonObject);
          child.ChildIngestionCandidates = ch.ChildIngestionCandidates;

          if (child.id === evtId) {
            child.status = ch.Status;
            child.internal.timestamp = new Date(child.internal.timestamp);
            child.internal.event_type = eventTypes?.find((x) => x.value === child.event_type);
            return child;
          }
          if (child.ChildIngestionCandidates && child.ChildIngestionCandidates.length > 0) {
            for (const grandc of child.ChildIngestionCandidates) {
              const grandEvent = JSON.parse(grandc.JsonObject);
              grandEvent.ChildIngestionCandidates = grandc.ChildIngestionCandidates;

              if (grandEvent.id === evtId) {
                grandEvent.status = grandc.Status;
                grandEvent.internal.timestamp = new Date(grandEvent.internal.timestamp);
                grandEvent.internal.event_type = eventTypes?.find((x) => x.value === grandEvent.event_type);
                return grandEvent;
              }
            }
          }
        }
      }
    }
    return null; // Return null if no match is found
  };

  const tableProps = {
    title,
    breakpoints,
    setLoading,
    loading,
    setMode,
    eventId,
    setEventId,
    setEventTypeToActOn,
    eventTypeToCreate,
    setEventTypeToCreate,
    sort,
    setSort,
    setIsDuplicated,
    isInitialRender,
    expandedRowEntityId,
    setExpandedRowEntityId,
    isAccordionExpanded,
    setIsAccordionExpanded,
  };

  const editProps = {
    setMode,
    mode,
    setEventId,
    eventId,
    setLoading,
    loading,
    //fullData,
    setEventTypeToActOn,
    isProcessed,
    isRoute,
    isDuplicated,
    fromMenu,
    findEvent,
    breadcrumbName: BREAD_CRUMB_NAMES.PROCESSED_EVENTS_LIST,
  };

  if (mode === FORM_MODE.LIST) return <ProcessedEventTable {...tableProps} />;

  const eventTypeObj = eventTypeToActOn ?? eventTypeToCreate;
  let eventType = eventTypeObj?.value;
  if (phasedEventTypes.find((x) => x.value === eventType)) eventType = EventTypes.PhasedEvent;

  switch (eventType) {
    case EventTypes.RoadClosure:
      return <ProcessedRcEdit {...editProps} />;
    case EventTypes.PhasedEvent:
      return <ProcessedPhasedEdit {...editProps} eventType={eventTypeObj.value} />;
    case EventTypes.AvalancheOccurrence:
      return <OccurrenceEdit {...editProps} eventType={eventTypeObj.value} />;
    case EventTypes.AvalancheControl:
      return <AvalancheControlEdit {...editProps} eventType={eventTypeObj.value} />;
    case EventTypes.HazardForecast:
      return <ProcessedHazardForecastEdit {...editProps} eventType={eventTypeObj.value} />;

    default:
      toast.error(`${eventType} is not supported yet`, { autoClose: 5000 });
      setMode(FORM_MODE.LIST);
  }
};

export default AvalancheProcessedEvent;

export function AvalancheProcessedEventWrapper() {
  const location = useLocation();
  const [locationKey, setLocationKey] = useState();
  const isInitialRender = useRef(true);

  useEffect(() => {
    setLocationKey(location?.key);
  }, [location]);

  return (
    <AwpContainer>
      <AvalancheEventContextProvider>
        <AvalancheTableFilterContextProvider>
          <AvalancheProcessedEvent key={locationKey} isInitialRender={isInitialRender} />
        </AvalancheTableFilterContextProvider>
      </AvalancheEventContextProvider>
    </AwpContainer>
  );
}
